import { get, compose, identity } from 'lodash/fp';
import { createSelector } from 'reselect';
import { getStringProp } from '@mews/fp';
import { createAsyncActionTypes, createAction } from '@mews/framework';
import { ApplicationState, CommanderThunkAction } from '@mews-commander/types';
import { MessageThread } from '@mews-commander/messages';
import { getOrEmptyObject } from '@mews-commander/utils';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import parentSelector from '../selector';
import prefix from '../prefix';
import { createScopedEntitiesReducer, createScopedBaseEntitySelectors } from './utils';
import { createCurrentEnterpriseScopedAction } from './scopedActionCreators';

export const NAME = 'threads';

export const THREADS_FETCH = prefix('THREADS_FETCH');
export const [THREADS_FETCH_STARTED, THREADS_FETCH_SUCCEEDED, THREADS_FETCH_FAILED] =
    createAsyncActionTypes(THREADS_FETCH);

export const THREAD_ADD_SUCCEEDED = prefix('THREAD_ADD_SUCCEEDED');
export const THREAD_DELETE_SUCCEEDED = prefix('THREAD_DELETE_SUCCEEDED');
export const THREAD_UPDATE_SUCCEEDED = prefix('THREAD_UPDATE_SUCCEEDED');

export const [getThreads, getThreadsByIds, getThreadsByOwnerId, getThread] = createScopedBaseEntitySelectors<
    ApplicationState,
    MessageThread,
    typeof NAME,
    'enterpriseId'
>(parentSelector, NAME, 'enterpriseId');

export const getCurrentEnterpriseThreads = createSelector(
    getCurrentEnterpriseId,
    identity<ApplicationState>,
    (enterpriseId, state) => getThreads(state, { enterpriseId })
);

export const getCurrentEnterpriseThreadById = createSelector(
    // @ts-expect-error $TSFixMeError: No overload matches this call.
    getStringProp('id'),
    getCurrentEnterpriseThreads,
    getOrEmptyObject
);

export const getThreadOriginalSender = compose(get('originalSender'), getCurrentEnterpriseThreadById);

export const setThreads =
    (entities: MessageThread[]): CommanderThunkAction =>
    (dispatch) => {
        const enterpriseId = getCurrentEnterpriseId();
        const meta = { scope: enterpriseId };

        return dispatch(createAction(THREADS_FETCH_SUCCEEDED, { entities }, meta));
    };

export const setThread = (thread: MessageThread) => createCurrentEnterpriseScopedAction(THREAD_ADD_SUCCEEDED, thread);

export const updateThread = (threadId: string, data: Partial<MessageThread>) =>
    createCurrentEnterpriseScopedAction(THREAD_UPDATE_SUCCEEDED, { id: threadId, ...data });

export const threadsReducer = createScopedEntitiesReducer({
    FETCH_SUCCESS: THREADS_FETCH_SUCCEEDED,
    UPDATE_SUCCESS: THREAD_UPDATE_SUCCEEDED,
    ADD_SUCCESS: THREAD_ADD_SUCCEEDED,
    DELETE_SUCCESS: THREAD_DELETE_SUCCEEDED,
});
